/**
 * Sign a packet — several documents, one sitting.
 *
 * The backend session flow has existed since August and nothing rendered it,
 * so `/sign/session/:token` was a route the API served and the app did not
 * have. Every multi-document send would have emailed a link to a blank page.
 *
 * The shape of the thing: an onboarding pack or a compliance packet is one act
 * by one person. They read each document in turn, then sign once, and every
 * document in the folder receives that signature with its own record, hash and
 * signed copy. So this is a checklist with one button at the bottom, not five
 * separate signing pages wearing a progress bar.
 *
 * The sign button is gated by the SERVER (`canSign`), not by this component
 * adding up checkboxes. "I have reviewed these" must mean the documents were
 * actually fetched for viewing, or the review step is a box someone ticks
 * about files they never opened.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText, CheckCircle2, Circle, Loader2, ShieldCheck, Hash, Lock, AlertCircle,
} from 'lucide-react';
import { TENANT } from '../config';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';
const PLATFORM_NAME = TENANT.branding.name;

interface SessionDoc {
  signatureId: string;
  title: string;
  fileName: string | null;
  fileSize: number | null;
  status: string;
  reviewed: boolean;
  signedAt: string | null;
  documentHash: string | null;
  viewUrl: string;
}

interface Session {
  token: string;
  envelopeTitle: string | null;
  envelopeDescription: string | null;
  envelopeHash: string | null;
  signerName: string | null;
  riskTierLabel: string;
  requiresOtp: boolean;
  otpVerified: boolean;
  documents: SessionDoc[];
  progress: { total: number; reviewed: number; signed: number };
  canSign: boolean;
  complete: boolean;
}

const kb = (bytes: number | null) =>
  (bytes ? `${Math.round(bytes / 1024).toLocaleString()} KB` : '');

/** Both ends of the digest, matching what is stamped on the signed page. */
const shortHash = (h: string | null) =>
  (h && h.length > 20 ? `${h.slice(0, 8)}...${h.slice(-8)}` : h || '');

const SignSession: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);

  const [opening, setOpening] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/esign/session/${token}`);
      const body = await res.json();
      if (!res.ok || !body?.success) {
        setError(body?.message || 'This signing link is not valid.');
        return;
      }
      setSession(body.data);
      if (body.data.complete) setDone(true);
      if (body.data.signerName && !fullName) setFullName(body.data.signerName);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  // fullName is intentionally not a dependency: refetching after a signature
  // must not clobber what the signer has typed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /**
   * Open one document.
   *
   * Fetching it is what marks it reviewed — the server records the view when
   * it streams the bytes, which is why this goes through fetch rather than
   * dropping a link on the page and hoping.
   */
  const openDocument = async (doc: SessionDoc) => {
    setOpening(doc.signatureId);
    try {
      const res = await fetch(`${API_BASE}/esign/session/${token}/documents/${doc.signatureId}`);
      if (!res.ok) throw new Error('could not open');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoked on a delay: revoking immediately races the new tab's load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      await load();
    } catch {
      setError('That document could not be opened. Nothing has been signed.');
    } finally {
      setOpening(null);
    }
  };

  const requestOtp = async () => {
    setOtpSending(true);
    try {
      const res = await fetch(`${API_BASE}/esign/session/${token}/request-otp`, { method: 'POST' });
      if (res.ok) setOtpRequested(true);
      else setError('Could not send the verification code.');
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    const res = await fetch(`${API_BASE}/esign/session/${token}/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ otp: otp.trim() }),
    });
    const body = await res.json();
    if (res.ok && body?.success) { setOtp(''); await load(); } else {
      setError(body?.message || 'That code was not accepted.');
    }
  };

  const sign = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/esign/session/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // signerEmail is deliberately not sent. The invited address is the
        // record of who was asked, and it is not the signer's to change.
        body: JSON.stringify({ signerName: fullName.trim() }),
      });
      const body = await res.json();
      if (!res.ok || !body?.success) {
        setError(body?.message || 'The documents could not be signed.');
        return;
      }
      setDone(true);
      await load();
    } catch {
      setError('Could not reach the server. Nothing has been signed.');
    } finally {
      setSubmitting(false);
    }
  };

  const namesMatch = fullName.trim().length > 1
    && fullName.trim().toLowerCase() === confirmName.trim().toLowerCase();
  const ready = Boolean(session?.canSign) && namesMatch && consent && !submitting;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{PLATFORM_NAME}</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const s = session as Session;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{PLATFORM_NAME}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {s.documents.length > 1 ? 'Documents for Signature' : 'Document Signature Request'}
          </p>
        </header>

        {done ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {s.documents.length > 1
                ? `All ${s.documents.length} documents signed`
                : 'Document signed'}
            </h2>
            <p className="text-sm text-gray-600">
              A copy of each signed document has been recorded. You may close this page.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
              <div className="flex items-start justify-between gap-4 mb-1">
                <h2 className="font-semibold text-gray-900">
                  {s.envelopeTitle || 'Documents for signature'}
                </h2>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0">
                  {s.riskTierLabel}
                </span>
              </div>
              {s.envelopeDescription && (
                <p className="text-sm text-gray-600 mb-3">{s.envelopeDescription}</p>
              )}
              <p className="text-sm text-gray-600">
                Open each document below. Once you have reviewed them all you can sign
                {s.documents.length > 1 ? ' every document at once' : ''}.
              </p>
              <p className="text-xs text-gray-500 mt-3 tabular-nums">
                {s.progress.reviewed} of {s.progress.total} reviewed
              </p>
            </div>

            <ol className="space-y-2 mb-6">
              {s.documents.map((doc, i) => {
                const isSigned = doc.status === 'signed';
                return (
                  <li
                    key={doc.signatureId}
                    className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-3"
                  >
                    <div className="shrink-0 mt-0.5">
                      {isSigned || doc.reviewed
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        : <Circle className="w-5 h-5 text-gray-300" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {i + 1}. {doc.title}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {kb(doc.fileSize)}
                        {doc.documentHash && (
                          <span className="ml-2 font-mono">
                            <Hash className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                            {shortHash(doc.documentHash)}
                          </span>
                        )}
                      </div>
                      {isSigned && (
                        <span className="text-xs text-emerald-700">Signed</span>
                      )}
                    </div>
                    {!isSigned && (
                      <button
                        type="button"
                        onClick={() => openDocument(doc)}
                        disabled={opening === doc.signatureId}
                        className="shrink-0 text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {opening === doc.signatureId
                          ? 'Opening…'
                          : (doc.reviewed ? 'View again' : 'Review')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>

            {s.requiresOtp && !s.otpVerified && (
              <div className="bg-white border border-amber-200 rounded-lg p-5 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <h3 className="text-sm font-semibold text-gray-900">Verify your identity</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  These documents need a one-time code before they can be signed.
                </p>
                {!otpRequested ? (
                  <button
                    type="button" onClick={requestOtp} disabled={otpSending}
                    className="text-sm px-4 py-1.5 rounded bg-gray-900 text-white disabled:opacity-50"
                  >
                    {otpSending ? 'Sending…' : 'Send me a code'}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={otp} onChange={(e) => setOtp(e.target.value)}
                      placeholder="6-digit code" inputMode="numeric"
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm w-40 tracking-widest"
                    />
                    <button
                      type="button" onClick={verifyOtp}
                      className="text-sm px-4 py-1.5 rounded bg-gray-900 text-white"
                    >
                      Verify
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                Sign {s.documents.length > 1 ? `all ${s.documents.length} documents` : 'this document'}
              </h3>

              <label className="block text-sm text-gray-700 mb-1">
                Type Your Full Legal Name <span className="text-red-500">*</span>
              </label>
              <input
                value={fullName} onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. John Michael Smith"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-1"
              />
              <p className="text-xs text-gray-500 mb-4">
                Type your full legal name exactly as it appears on your government-issued ID.
              </p>

              <label className="block text-sm text-gray-700 mb-1">
                Confirm Full Legal Name <span className="text-red-500">*</span>
              </label>
              <input
                value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                placeholder="Re-type your full legal name"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              />

              <label className="flex items-start gap-2 mb-5">
                <input
                  type="checkbox" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  I have reviewed {s.documents.length > 1 ? 'these documents' : 'this document'} and
                  agree to sign {s.documents.length > 1 ? 'them' : 'it'} electronically. I understand
                  this constitutes a legally binding signature under the ESIGN Act and UETA.
                </span>
              </label>

              {!s.canSign && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                  Open every document above before signing.
                </p>
              )}
              {error && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
                  {error}
                </p>
              )}

              <button
                type="button" onClick={sign} disabled={!ready}
                className="w-full py-2.5 rounded bg-emerald-700 text-white font-semibold disabled:opacity-40 hover:bg-emerald-800"
              >
                {submitting
                  ? 'Signing…'
                  : `I Agree & Sign ${s.documents.length > 1 ? `${s.documents.length} Documents` : 'Document'}`}
              </button>

              <p className="text-xs text-gray-500 text-center mt-3 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Your IP address and browser information will be recorded for audit purposes.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignSession;
